import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useEffect } from 'react';
import { useTextHighlight } from '../utils/highlightUtils';

const Class18GuidedPractice01 = () => {
    const navigate = useNavigate();

    const highlightRef = useRef(null);
    useTextHighlight(highlightRef);

    const [showHelp, setShowHelp] = useState(false);

    const questionRefs = useRef([]);
    const rightColRef = useRef(null);

    // Make sure your answers state exists
    const [answers, setAnswers] = useState(Array(20).fill(''));
    const [currentQuestion, setCurrentQuestion] = useState(14);
    const onNext = () => {
        setCurrentQuestion(prev => Math.min(prev + 1, 19));
    };

    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);

    // Timer state
    const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
    const [showSeconds, setShowSeconds] = useState(false);

    // Add missing state for drag-and-drop headings
    const [draggedHeading, setDraggedHeading] = useState(null);
    const [selectedHeading, setSelectedHeading] = useState(null);
    const [headingAssignments, setHeadingAssignments] = useState({
        14: null,
        15: null,
        16: null,
        17: null,
        18: null,
        19: null,
        20: null
    });
    const headingList = [
        "A period in cold conditions before the technology is assessed",
        "Marketing issues lead to failure",
        "Good and bad aspects of steam technology are passed on",
        "A possible solution to the issues of today",
        "Further improvements lead to commercial orders",
        "Positive publicity at last for this quiet, clean, fast vehicle",
        "A disappointing outcome for customers",
        "A better option than the steam car arises"
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 0) {
                    clearInterval(timer);
                    navigate('/');  // Redirect to home
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [navigate]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (showSeconds) {
            return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
        }
        return `${mins} minutes remaining`;
    };

    const handleStartTest = () => {
        navigate(`/guided-practice-01-form`);
    };

    const handlePlayPause = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        setCurrentTime(audioRef.current.currentTime);
    };

    const handleLoadedMetadata = () => {
        if (!audioRef.current) return;
        setDuration(audioRef.current.duration);
    };

    const handleSeek = (e) => {
        if (!audioRef.current) return;
        const time = parseFloat(e.target.value);
        audioRef.current.currentTime = time;
        setCurrentTime(time);
    };

    const handleVolumeChange = (e) => {
        if (!audioRef.current) return;
        const vol = parseFloat(e.target.value);
        setVolume(vol);
        audioRef.current.volume = vol;
        setIsMuted(vol === 0);
    };

    const handleMute = () => {
        if (!audioRef.current) return;
        if (isMuted) {
            audioRef.current.muted = false;
            setIsMuted(false);
            audioRef.current.volume = volume;
        } else {
            audioRef.current.muted = true;
            setIsMuted(true);
        }
    };

    const GreenCheck = () => (
        <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="inline-block"
        >
            <path
                d="M7 11L9 13.5L16 7"
                stroke="#7DD77D"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );


    return (
        <div className="min-h-screen bg-white flex flex-col font-[Arial]">
            {/* Help Modal */}
            <HelpModal
                isOpen={showHelp}
                onClose={() => setShowHelp(false)}
            />
            {/* Top Bar (fixed) */}
            <div className="sticky top-0 z-30 bg-white">
                <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
                    {/* Left + Center combined on left side */}
                    <div className="flex flex-col justify-center space-y-0.5 pl-6">
                        <span className="text-[14px] text-left text-black font-bold leading-tight">
                            HEXA'S Education
                        </span>
                        <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
                            <span
                                onMouseEnter={() => setShowSeconds(true)}
                                onMouseLeave={() => setShowSeconds(false)}
                                className="cursor-pointer"
                            >
                                {formatTime(timeRemaining)}
                            </span>
                        </div>
                    </div>
                    {/* Right: Icons and Finish Button */}
                    <div className="flex items-center space-x-5">
                        <button
                            onClick={() => navigate('/')}
                            className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
                        >
                            Back Home
                        </button>
                        <button
                            onClick={() => setShowHelp(true)}
                            className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
                        >
                            Finish test
                        </button>
                        <div className="flex items-center space-x-8 pl-2 pr-2">
                            {/* WiFi Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 717.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            {/* Notification Bell Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                </svg>
                            </div>
                            {/* Hamburger Menu Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            {/* Pen/Edit Icon */}
                            <div className="w-10 h-10 flex items-center justify-center">
                                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Title Bar (fixed under top bar) */}
                <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
                    {/* Left: Part Title */}
                    <div className="flex-1 px-4 py-2 text-left">
                        <h2 className="text-[23px] text-black">Part 1</h2>
                        <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
                    </div>
                    {/* Center: Absolutely Positioned Logo */}
                    <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <img
                            src={HEXASLogo}
                            alt="HEXA'S Logo"
                            className="h-[50px] object-contain"
                        />
                    </div>
                    {/* Right: Empty space for consistency */}
                    <div className="flex items-center justify-end w-[300px] pr-4">
                        {/* Empty space - no audio player for reading */}
                    </div>
                </div>
            </div>
            {/* Main content (scrollable) */}
            <div className="flex-1 flex flex-col items-center justify-start w-full">
                <div ref={highlightRef}
                    className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
                    style={{ height: 'calc(100vh - 180px)' }}
                >
                    {/* Left: Reading Passage */}
                    <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
                        <div className="mb-8">
                            <h2 className="text-[16px] font-bold mb-4 text-center">RAPID POLICE RESPONSE</h2>

                            <div className="text-[14px] space-y-4 text-left">
                                {/* Section A */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[0] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[14] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[14] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 14: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 14: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[14] !== null
                                            ? headingList[headingAssignments[14]]
                                            : '14'}
                                    </span>
                                    <br />
                                    <p>
                                        Police departments in the United States and Canada see it as central to their role that they respond to calls for help as quickly as possible. This ability to react fast has been greatly improved with the aid of technology. The telephone and police radio, already long in use, assist greatly in the reduction of police response time. In more recent times there has been the introduction of the '911' emergency system, which allows the public easier and faster contact with police, and the use of police computer systems, which assist police in planning patrols and assigning emergency requests to the police officers nearest to the scene of the emergency.
                                    </p>
                                </div>

                                {/* Section B */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[1] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[15] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[15] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 15: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 15: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[15] !== null
                                            ? headingList[headingAssignments[15]]
                                            : '15'}
                                    </span>
                                    <br />
                                    <p>
                                        An important part of police strategy, rapid police response is seen by police officers and the public alike as offering tremendous benefits. The more obvious ones are the ability of police to apply first-aid life-saving techniques quickly and the greater likelihood of arresting people who may have participated in a crime. It aids in identifying those who witnessed an emergency or crime, as well as in collecting evidence. The overall reputation of a police department, too, is enhanced if rapid response is consistent, and this in itself promotes the prevention of crime. Needless to say, rapid response offers the public some degree of satisfaction in its police force.
                                    </p>
                                </div>

                                {/* Section C */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[2] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[16] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[16] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 16: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 16: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[16] !== null
                                            ? headingList[headingAssignments[16]]
                                            : '16'}
                                    </span>
                                    <br />
                                    <p>
                                        While these may be the desired consequences of rapid police response, actual research has not shown it to be quite so beneficial. For example, it has been demonstrated that rapid response leads to a greater likelihood of arrest only if responses are in the order of 1-2 minutes after a call is received by the police. When response times increase to 3-4 minutes — still quite a rapid response — the likelihood of an arrest is substantially reduced. Similarly, in identifying witnesses to emergencies or crimes, police are far more likely to be successful if they arrive at the scene no more than four minutes, on average, after receiving a call for help. Yet both police officers and the public define 'rapid response' as responding up to 10-12 minutes after calling the police for help.
                                    </p>
                                </div>

                                {/* Section D */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[3] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[17] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[17] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 17: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 17: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[17] !== null
                                            ? headingList[headingAssignments[17]]
                                            : '17'}
                                    </span>
                                    <br />
                                    <p>
                                        Should police assume all the responsibility for ensuring a rapid response? Studies have shown that people tend to delay after an incident occurs before contacting the police. A crime victim may be injured and thus unable to call for help, for example, or no telephone may be available at the scene of the incident. Often, however, there is no such physical barrier to calling the police. Indeed, it is very common for crime victims to call their parents, their minister, or even their insurance company first. When the police are finally called in such cases, the effectiveness of even the most rapid of responses is greatly diminished.
                                    </p>
                                </div>

                                {/* Section E */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[4] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[18] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[18] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 18: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 18: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[18] !== null
                                            ? headingList[headingAssignments[18]]
                                            : '18'}
                                    </span>
                                    <br />
                                    <p>
                                        The effectiveness of rapid response also needs to be seen in light of the nature of the crime. For example, when someone rings the police after discovering their television set has been stolen from their home, there is little point, in terms of identifying those responsible for the crime, in ensuring a very rapid response. It is common in such burglary or theft cases that the victim discovers the crime hours, days, even weeks after it has occurred. When the victim is directly involved in the crime, however, as in the case of a robbery, rapid response, provided the victim was quickly able to contact the police, is more likely to be advantageous. Based on statistics comparing crimes that are discovered and those in which the victim is directly involved, Spelman and Brown (1981) suggest that three in four calls to police need not be met with rapid response.
                                    </p>
                                </div>

                                {/* Section F */}
                                <div className="mb-4">
                                    <span
                                        ref={el => questionRefs.current[5] = el}
                                        className="inline-block align-middle rounded bg-[#E0F2FE] border border-[#7DD3FC] shadow-sm px-3 py-0.5 font-bold text-[15px] text-black mb-1 cursor-pointer transition-colors focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                        style={{
                                            minWidth: 200,
                                            textAlign: 'center',
                                            marginBottom: 4,
                                            opacity: draggedHeading !== null ? 0.85 : 1,
                                            borderStyle: draggedHeading !== null ? 'dashed' : 'solid'
                                        }}
                                        tabIndex={0}
                                        title={headingAssignments[19] !== null ? 'Click to clear' : 'Drop a heading here'}
                                        onClick={() => {
                                            if (headingAssignments[19] !== null) {
                                                setHeadingAssignments(a => ({ ...a, 19: null }));
                                            }
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            const headingIdx = Number(e.dataTransfer.getData('headingIdx'));
                                            if (!isNaN(headingIdx)) {
                                                setHeadingAssignments(a => ({ ...a, 19: headingIdx }));
                                            }
                                            setDraggedHeading(null);
                                        }}
                                    >
                                        {headingAssignments[19] !== null
                                            ? headingList[headingAssignments[19]]
                                            : '19'}
                                    </span>
                                    <br />
                                    <p>
                                        It becomes clear that the importance of response time in collecting evidence or catching criminals after a crime must be weighed against a variety of factors. Yet because police department officials assume the public strongly demands rapid response, they believe that every call to the police should be met with it. Studies have shown, however, that while the public wants quick response, more important is the information given by the police to the person asking for help. If a caller is told the police will arrive in five minutes but in fact it takes ten minutes or more, waiting the extra time can be extremely frustrating. But if a caller is told he or she will have to wait 10 minutes and the police indeed arrive within that time, the caller is normally satisfied. Thus, rather than emphasizing rapid response, the focus of energies should be on establishing realistic expectations in the caller and making every effort to meet them.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>





                    {/* Right: List of Headings */}
                    <div
                        ref={rightColRef}
                        className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto"
                    >
                        <div className="mb-10 w-full">
                            <h3 className="text-[15px] font-bold mb-4 text-left">Questions 14 – 20</h3>
                            <p className="text-[14px] mb-6 text-left">
                                The text has seven sections. Choose the correct heading for each section from the list below. Drag and drop the headings into the correct boxes on the left.
                            </p>

                            <div className="mb-6">
                                <p className="text-[16px] font-bold mb-4">List of Headings</p>
                                <div className="flex flex-col gap-2">
                                    {[
                                        "i      A period in cold conditions before the technology is assessed",
                                        "ii     Marketing issues lead to failure",
                                        "iii    Good and bad aspects of steam technology are passed on",
                                        "iv    A possible solution to the issues of today",
                                        "v     Further improvements lead to commercial orders",
                                        "vi    Positive publicity at last for this quiet, clean, fast vehicle",
                                        "vii   A disappointing outcome for customers",
                                        "viii  A better option than the steam car arises"
                                    ].map((heading, idx) => {
                                        const assigned = Object.values(headingAssignments).includes(idx);
                                        if (assigned) return null; // Only show unassigned headings
                                        const isSelected = selectedHeading === idx;

                                        return (
                                            <div
                                                key={heading}
                                                className={`font-bold rounded-lg shadow-sm px-4 py-1 text-[14px] text-black select-none border border-[#cfd9e4] cursor-pointer transition-all
                    ${isSelected ? 'bg-blue-100 border-blue-400' : 'bg-transparent hover:bg-[#e6f0fa]/40'}`}
                                                draggable
                                                onDragStart={e => {
                                                    setDraggedHeading(idx);
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    e.dataTransfer.setData('headingIdx', String(idx));
                                                }}
                                                onDragEnd={() => setDraggedHeading(null)}
                                                onClick={() => setSelectedHeading(idx)}
                                            >
                                                {heading}
                                            </div>
                                        );
                                    })}
                                </div>

                                
                            </div>
                        </div>
                    </div>









                </div>
            </div>
            {/* Bottom Navigation (fixed footer) */}
            <div className="sticky bottom-0 left-0 w-full bg-[#D7DFEC] border-t border-gray-300 z-30 flex items-center justify-between px-4 py-3">
                {/* Left: Question Buttons */}
                <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((questionNumber) => (
                        <button
                            key={questionNumber}
                            onClick={() => setCurrentQuestion(questionNumber)}
                            className={`w-7 h-7 mx-0.5 rounded text-[12px] font-bold flex items-center justify-center ${currentQuestion === questionNumber
                                ? 'bg-blue-500 text-white'
                                : 'bg-black text-white'
                                }`}
                        >
                            {questionNumber}
                        </button>
                    ))}
                </div>

                {/* Right: Arrows */}
                <div className="flex items-center space-x-2">
                    {/* Backward Arrow */}
                    <button
                        onClick={() => {
                            setCurrentQuestion((prev) => Math.max(1, prev - 1));
                        }}
                        className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 1 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
                            } text-white`}
                        title="Previous Question"
                        disabled={currentQuestion === 1}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            className="w-4 h-4"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Forward Arrow */}
                    <button
                        onClick={() => {
                            setCurrentQuestion((prev) => Math.min(10, prev + 1));
                        }}
                        className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 10 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
                            } text-white`}
                        title="Next Question"
                        disabled={currentQuestion === 10}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            className="w-4 h-4"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Class18GuidedPractice01;
